import { env } from '@/lib/env'
import {
  AuthenticationResult,
  Configuration,
  EventMessage,
  EventType,
  LogLevel,
  PublicClientApplication,
  RedirectRequest,
} from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'

export const msalConfig: Configuration = {
  auth: {
    clientId: env.VITE_CLIENT_ID,
    authority: 'https://login.microsoftonline.com/' + env.VITE_TENANT_ID,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error('MSAL ERROR:', message)
            return
          case LogLevel.Info:
            console.info('MSAL INFO:', message)
            return
          case LogLevel.Verbose:
            console.debug('MSAL VERBOSE:', message)
            return
          case LogLevel.Warning:
            console.warn('MSAL WARNING:', message)
            return
        }
      },
    },
  },
}

export const loginRequest: RedirectRequest = {
  scopes: [`${env.VITE_DYNAMICS_API_BASE_URL}/.default`],
  state: window.location.search, // Store query params in state
}

export const msalInstance = new PublicClientApplication(msalConfig)

export function getIsAuthenticated() {
  return (
    !!msalInstance.getActiveAccount() ||
    msalInstance.getAllAccounts().length > 0 ||
    !!msalInstance.getAccount({ tenantId: env.VITE_TENANT_ID })
  )
}

export function useIsAuthenticated() {
  const { instance: msalInstance } = useMsal()
  return (
    !!msalInstance.getActiveAccount() ||
    msalInstance.getAllAccounts().length > 0 ||
    !!msalInstance.getAccount({ tenantId: env.VITE_TENANT_ID })
  )
}

msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const { account } = event.payload as AuthenticationResult
    msalInstance.setActiveAccount(account)

    // Set authenticated user context for App Insights telemetry
    if (account) {
      import('@/lib/telemetry')
        .then(({ setAuthenticatedUser }) => {
          setAuthenticatedUser(account.localAccountId, account.tenantId)
        })
        .catch(() => {})
    }

    // Handle query parameter restoration after successful login
    const payload = event.payload as AuthenticationResult
    if (payload.state) {
      // The state contains our original query parameters
      const newUrl = `${window.location.pathname}${payload.state}`
      window.location.replace(newUrl)
    }
  }
})
